import { node } from "prop-types";
import { useLocation } from "react-router-dom";

import styled from "styled-components";

import { nextFlocknoteIsNew } from "Dashboard/components/Greeting/constants";
import { useAppIsReady } from "Flocknote/stateHooks";

import {
  useEffectiveIsExpanded,
  useIsFullScreen,
  usePrimaryNavWidth,
} from "NavBar/stateHooks";
import {
  fullScreenBorderRadius,
  fullScreenMargin,
  navigationGradientColors,
  springAnimation,
} from "NavBar/styleConstants";
import { marketingColors } from "UI/colors";
import { addOpacity } from "Utilities/Colors/colorUtils";
import { deepShadow } from "Utilities/interpolations/shadow";
import { motion } from "motion/react";

import dynWidth from "Utilities/interpolations/dynWidth";

import NavToggle from "./NavToggle";

const NavPanel = ({ children }) => {
  const { pathname } = useLocation();
  const isFullScreen = useIsFullScreen();
  const primaryNavWidth = usePrimaryNavWidth();
  const [isExpanded] = useEffectiveIsExpanded();
  const isAppReady = useAppIsReady();

  const shouldSwoop = nextFlocknoteIsNew && pathname === "/action-center";
  // Only swoop in on action-center with welcome animation; elsewhere show immediately
  const shouldShow = shouldSwoop ? isAppReady && isExpanded : isExpanded;

  const initialX = shouldShow ? 0 : -primaryNavWidth;

  return (
    <Wrapper
      $width={primaryNavWidth}
      data-cy="NavBar_NavPanel_Wrapper"
      initial={{ x: initialX }}
      animate={{ x: shouldShow ? 0 : -primaryNavWidth }}
      transition={springAnimation}
    >
      <NavToggle data-cy="NavBar_NavToggle" />
      <Background $isFullScreen={isFullScreen}>{children}</Background>
    </Wrapper>
  );
};

NavPanel.propTypes = { children: node };

const fullScreenStyles = ({ $isFullScreen }) => {
  let styles = "";
  if ($isFullScreen) {
    styles += `border-radius: ${fullScreenBorderRadius}px;`;
    styles += `margin: ${fullScreenMargin - 1}px;`;
    styles += `height: calc(var(--full-viewport-height, 100vh) - ${
      fullScreenMargin * 2
    }px);`;
    styles += `border: 1px solid ${addOpacity(marketingColors.gray, 0.15)};`;
    styles += deepShadow;
  } else {
    styles += "border-radius: 0px;";
    styles += `margin: 0px;`;
    styles += `height: var(--full-viewport-height, 100vh);`;
  }

  styles += "transition: border-radius 300ms, margin 300ms, height 300ms;";
  return styles;
};

const Background = styled.div`
  background: ${({ $isFullScreen }) =>
    $isFullScreen
      ? `radial-gradient(
          calc(60vh) at 425px 60%,
          ${navigationGradientColors[0]},
          ${navigationGradientColors[1]}
        )`
      : "transparent"};
  ${fullScreenStyles}
  transition: background 300ms, border-radius 300ms, margin 300ms, height 300ms;
`;

const Wrapper = styled(motion.div)`
  ${dynWidth}
  height: var(--full-viewport-height, 100vh);
  position: fixed;
  left: 0;
  top: 0;
  overflow-x: visible;
  pointer-events: auto;
  border-radius: ${({ $isFullScreen }) =>
    $isFullScreen ? fullScreenBorderRadius : 0}px;
  transition: border-radius 300ms;
  z-index: 3000;
`;

export default NavPanel;
